import { inject, type InjectionKey, type Ref } from 'vue'

// types
import type { CalendarPassThrough, CalendarPassThroughEntry } from '@/types/components/calendar'

/** Shared by every empty lookup so templates never re-render on a fresh object */
const EMPTY_ENTRY: CalendarPassThroughEntry = Object.freeze({})

/** Injection key the root Calendar uses to share its `pt` prop with the views */
export const CALENDAR_PASS_THROUGH_KEY: InjectionKey<Ref<CalendarPassThrough>> = Symbol('calendarPassThrough')

/**
 * Reads one section out of a passthrough object.
 * @param passThrough - The calendar's `pt` prop
 * @param section - Section to look up
 * @returns The section's class and style, or an empty entry when unset
 */
export function resolvePassThrough(
  passThrough: CalendarPassThrough | undefined | null,
  section: keyof CalendarPassThrough
): CalendarPassThroughEntry {
  return passThrough?.[section] ?? EMPTY_ENTRY
}

/**
 * Gives a calendar sub-component access to the root's styling passthrough.
 * @returns A lookup that can be spread onto an element with `v-bind`
 */
export function useCalendarPassThrough(): (section: keyof CalendarPassThrough) => CalendarPassThroughEntry {
  const passThrough = inject(CALENDAR_PASS_THROUGH_KEY, null)

  return (section: keyof CalendarPassThrough) => resolvePassThrough(passThrough?.value, section)
}
